/**
 * Room join form (UX-SPEC §4): opening `/r/CODE` without a stored seat shows
 * who sent the invite ("Ava invited you…", from `room:peek`), the room's
 * settings, a name field and Join, over the usual footer (theme, sound,
 * version). A missing or full room replaces the form with a friendly notice
 * and a way back — as soon as the peek says so, not only after Join.
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { NAME_MAX_LENGTH, type RoomPeek } from '@wordloom/engine';
import { focusFirstInvalid } from '../lib/focus';
import { turnTimeLabel } from '../lib/time';
import { leave, joinWithName, peekInvite, storedName } from '../net/session';
import { useRoomStore } from '../state/roomStore';
import { IconArrowRight, IconDoor, IconUser } from './Icons';
import { nameError } from './Lobby';
import { SoundToggle } from './SoundToggle';
import { ThemeToggle } from './ThemeToggle';
import { VersionPill } from './VersionPill';
import { Wordmark } from './Wordmark';
import './JoinForm.css';

/** "1 min per turn · Ends after 2 passes each" */
export function inviteSettingsLine({ turnTimeLimitSec, passRoundsToEnd }: RoomPeek['settings']): string {
  const passes = `${passRoundsToEnd} ${passRoundsToEnd === 1 ? 'pass' : 'passes'} each`;
  return `${turnTimeLabel(turnTimeLimitSec)} · Ends after ${passes}`;
}

function JoinScreen({ children }: { children: ReactNode }) {
  return (
    <div className="join-screen">
      <main className="screen-center" id="main">
        {children}
      </main>
      <footer className="app-footer join-screen__footer">
        <ThemeToggle />
        <SoundToggle />
        <VersionPill />
      </footer>
    </div>
  );
}

export function JoinForm({ code }: { code: string }) {
  const joinError = useRoomStore((s) => s.joinError);
  const joining = useRoomStore((s) => s.joining);
  const [name, setName] = useState(() => storedName());
  const [nameErr, setNameErr] = useState('');
  const nameId = useId();
  const [peek, setPeek] = useState<RoomPeek | null>(null);

  useEffect(() => {
    let alive = true;
    setPeek(null);
    void peekInvite(code).then((p) => {
      if (alive) setPeek(p);
    });
    return () => {
      alive = false;
    };
  }, [code]);

  if (joinError?.code === 'ROOM_NOT_FOUND' || joinError?.code === 'ROOM_FULL') {
    const notFound = joinError.code === 'ROOM_NOT_FOUND';
    return (
      <JoinScreen>
        <section className="card notice" aria-labelledby="join-problem">
          <span className="notice__icon" aria-hidden="true">
            {notFound ? <IconDoor /> : <IconUser />}
          </span>
          <h1 id="join-problem">{notFound ? 'We couldn’t find that room' : 'This room is full'}</h1>
          <p>
            {notFound
              ? `Room ${code} doesn’t exist or has expired. Check the link, or start a new game.`
              : `Room ${code} already has two players. Ask for a new link, or start your own game.`}
          </p>
          <div className="notice__actions">
            <button type="button" className="btn btn--primary" autoFocus onClick={() => leave({ forget: true })}>
              Back to lobby
            </button>
          </div>
        </section>
      </JoinScreen>
    );
  }

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (joining) return;
    const form = e.currentTarget;
    const ne = nameError(name);
    // Commit the error first so the focused field is read with it.
    flushSync(() => setNameErr(ne));
    if (ne) {
      focusFirstInvalid(form);
      return;
    }
    await joinWithName(code, name);
  };

  const host = peek?.hostName;
  return (
    <JoinScreen>
      <form className="card join" onSubmit={(e) => void submit(e)} aria-labelledby="join-heading" noValidate>
        <Wordmark size="sm" />
        <h1 id="join-heading" className="join__title">
          {host ? (
            <>
              <span className="join__host">{host}</span> invited you to a Wordloom game
            </>
          ) : (
            'You’ve been invited to a Wordloom game'
          )}
        </h1>
        <p className="join__room">
          Room <span className="join__code">{code}</span>
        </p>
        {/* The line keeps its height while the peek is on its way (no jump). */}
        <p className="join__settings" aria-hidden={peek ? undefined : true}>
          {peek ? inviteSettingsLine(peek.settings) : '\u00a0'}
        </p>
        {/* The error sits outside the <label> so it is not read as part of the name. */}
        <div className="field join__field">
          <label className="field__label" htmlFor={nameId}>
            Your name
          </label>
          <input
            id={nameId}
            className="input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameErr) setNameErr('');
            }}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="nickname"
            placeholder="e.g. Maya"
            autoFocus
            enterKeyHint="go"
            aria-invalid={nameErr ? true : undefined}
            aria-describedby={nameErr ? 'join-name-err' : undefined}
          />
          {nameErr && (
            <span id="join-name-err" className="field__error">
              {nameErr}
            </span>
          )}
        </div>
        {joinError && (
          <p className="field__error" role="alert">
            {joinError.message}
          </p>
        )}
        <button type="submit" className="btn btn--primary btn--block join__cta" aria-disabled={joining || undefined}>
          {joining ? <span className="btn__spinner" aria-hidden="true" /> : <IconArrowRight />}
          Join game
        </button>
        <button type="button" className="btn btn--ghost btn--sm join__back" onClick={() => leave({ forget: false })}>
          Back to lobby
        </button>
      </form>
    </JoinScreen>
  );
}
